'use client';
import { getQueryParams } from '@/utils/sliceRoute';
import { useState } from 'react';
import { FaChevronDown, FaChevronUp, FaMinus, FaPlus } from 'react-icons/fa6';
type CategoryClickPayload = {
  category: string | null;
  type: string | null;
  good_for: string | null;
  brand: string | null;
};
interface Category {
  id: string | number;
  name: string;
  slug: string;
  counts?: number;
  icon?: string;
  banner?: string;
  is_top?: number;
  is_featured?: number;
  children?: Category[];
  order_level?: number;
  url?: string;
}

interface CategoryFilterProps {
  categories: Category[];
  onCategoryClick: (payload: CategoryClickPayload) => void;
  selectedCategory: string | null;
}

export default function CategoryFilter({
  categories = [],
  onCategoryClick,
  selectedCategory,
}: CategoryFilterProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openItems, setOpenItems] = useState<Record<string | number, boolean>>(
    {}
  );

  const handleCategoryClick = (category: Category, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    let params: Record<string, string> = {};
    if (category?.url && typeof category.url === 'string') {
      params = getQueryParams(category.url);
    }

    const selectedCategory = params.category ?? null;
    const selectedType = params.type ?? null;
    const good_for = params.good_for ?? null;
    const brand = params.brand ?? null;

    onCategoryClick({
      category: selectedCategory,
      type: selectedType,
      good_for: good_for,
      brand: brand,
    });
  };

  const toggleItem = (pathKey: string | number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenItems((prev) => ({ ...prev, [pathKey]: !prev[pathKey] }));
  };

  const handleCollapse = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsCollapsed(!isCollapsed);
  };

  const formatCategoryName = (category: Category) => {
    return `${category.name}${category.counts ? ` (${category.counts})` : ''}`;
  };

  const hasChildren = (item: Category) => {
    return item.children && item.children.length > 0;
  };

  const renderCategories = (
    items: Category[] = [],
    level = 0,
    parentPath = ''
  ) => {
    // const sortedItems = [...items].sort((a, b) => {
    //   const aOrder = a.order_level ?? Infinity;
    //   const bOrder = b.order_level ?? Infinity;
    //   return aOrder - bOrder;
    // });

    const sortedItems = items;

    return (
      <ul
        className={`category-filter__list ${level > 0 ? 'category-filter__list--child' : ''}`}
        style={{ paddingLeft: level > 0 ? `${8}px` : '0' }}
      >
        {sortedItems.map((item) => {
          const pathKey = `${parentPath}/${item.id}`; // unique path key

          const itemHasChildren = hasChildren(item);
          const isOpen = openItems[pathKey] || false;
          const isSelected = selectedCategory === item.slug;

          return (
            <li
              key={pathKey}
              className={`category-filter__item ${isSelected ? 'active' : ''}`}
            >
              <div
                className={`category-filter__item-header d-flex gap-2 justify-content-between align-items-center 
                  ${itemHasChildren ? 'has-children cursor-pointer' : 'cursor-pointer'}`}
                onClick={(e) => {
                  handleCategoryClick(item, e);
                  if (itemHasChildren) {
                    toggleItem(pathKey, e);
                  }
                }}
              >
                <span
                  className={`category-filter__item-name ${isSelected ? 'text-black fw-medium' : ''}`}
                >
                  {formatCategoryName(item)}
                </span>
                {itemHasChildren && (
                  <span
                    className="category-filter__item-toggle"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleItem(pathKey, e);
                    }}
                  >
                    {isOpen ? (
                      <FaChevronUp size={12} />
                    ) : (
                      <FaChevronDown size={12} />
                    )}
                  </span>
                )}
              </div>
              {itemHasChildren && isOpen && item.children && (
                <div className="category-filter__sub-list">
                  {renderCategories(item.children, level + 1, pathKey)}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  const safeCategories = categories || [];

  return (
    <div
      className={`sidebar-filter sidebar-filter--category sidebar-filter--collapsible mb-40 ${isCollapsed ? 'is-collapsed' : ''}`}
      style={{
        maxHeight: isCollapsed ? '60px' : 'none',
        overflow: isCollapsed ? 'hidden' : 'visible',
        transition: 'max-height 0.3s ease',
      }}
    >
      <div
        className="sidebar-filter__header d-flex flex-wrap gap-2 justify-between items-center cursor-pointer"
        onClick={handleCollapse}
      >
        <h3 className="sidebar-filter__title mb-0">Categories</h3>
        <button
          type="button"
          className="sidebar-filter__toggle border-0 bg-transparent"
          onClick={handleCollapse}
        >
          {isCollapsed ? <FaPlus size={16} /> : <FaMinus size={16} />}
        </button>
      </div>

      <div
        id="category-filter-content"
        className="sidebar-filter__content"
        style={{
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
          paddingRight: '8px',
        }}
      >
        {!isCollapsed && renderCategories(safeCategories)}
      </div>
    </div>
  );
}
